import { useTranslation } from 'react-i18next';
// pages/settings/Groups.tsx
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, Button, TextField, Box, Skeleton, Stack, Switch, FormControlLabel, CircularProgress } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/Toast";
import { getGroupByCompany, findGroup, createGroup, addMember, searchGroups } from "../../services/group/group";
import CustomPagination from "../../components/customPagination";
import { Search, GroupAdd } from "@mui/icons-material";
import { GroupMemberDataFromSearchResult, GroupDataFromSearchResult as GroupSearchResult } from "../../interfaces/group.interface";
// Componentes estilizados importados do seu arquivo de estilos
import {
  ContainerConfigGroup,
  InputWrapper,
  StyledInputSearchUser,
  SearchButton,
  ContainerButtonGroup,
  ListTable,
  StyledTh,
  StyledTdGroup,
  StyledTr,
  ContainerEditGroup,
  StyledContainerButtonToggle,
} from "./styles";
import GroupTable from "../../components/dataTable/groupTable";
import ModalAddGroup from "./modalAddGroup";
import ModalEditGroup from "./modalEditGroup";
import { User } from "../../interfaces/user.interface";
import { searchUsers } from "../../services/user/user";
import TableSkeleton from "../../components/skelletons/settings/TableSkeleton";
import { GroupStatus } from "../../enums/group";
import { getApiErrorMessage } from "../../services/utils/apiError";

type ErrorHandlerOptions = {
  redirectTo?: string;
  context?: 'admin' | 'user' | string;
};

export default function Groups() {
  const { t } = useTranslation('settings');
  const { user, SignOut } = useAuth();
  const { showToast } = useToast();

  const [searchTerm, setGroupTerm] = useState("");
  const [groupTableItemAnchorElement, setGroupTableItemAnchorElement] = useState<null | HTMLElement>(null);
  const [listGroupData, setListGroupData] = useState({
    total_items: 0,
    total_pages: 0,
    current_page: 1,
    data: [] as GroupSearchResult[],
  });

  const [modalEditGroupData, setModalEditGroupData] = useState<{
    show: boolean,
    group_id: string,
    group_name: string,
    group_status: GroupStatus,
    group_members: GroupMemberDataFromSearchResult[],
    responsible_users: GroupMemberDataFromSearchResult[],
  }>({
    show: false,
    group_id: '',
    group_name: '',
    group_status: GroupStatus.active,
    group_members: [],
    responsible_users: [],
  });

  const [loadingTable, setLoadingTable] = useState(true);
  const [thereIsUpdates, setThereIsUpdates] = useState(false);
  const [showInactiveGroups, setShowInactiveGroups] = useState(false);
  const [groupsViewLoading, setGroupsViewLoading] = useState(false);
  const [userList, setUserList] = useState<User[]>([]);
  const [groupList, setGroupList] = useState<GroupSearchResult[]>([]);
  const [showModalNewGroup, setShowModalNewGroup] = useState(false)
  const [debouncedGroupTerm, setDebouncedGroupTerm] = useState("");


  useEffect(() => {
    const loadData = async () => {
      try {
        setGroupsViewLoading(true);
        const company = user.company_visited?.id;
        if (!company) return redirectSessionExpired();

        const usersResult = await searchUsers({ company_id: company, statuses: [1, 2, 3, 4, 5] });
        const groupsResult = await searchGroups({
          company_id: company,
          groupsStatuses: showInactiveGroups ? [1, 2] : [1],
        });

        setUserList(usersResult.data);
        setGroupList(groupsResult.data);
        setListGroupData({
          total_items: groupsResult.total_items,
          total_pages: Math.ceil(groupsResult.total_items / 10),
          current_page: 1,
          data: groupsResult.data,
        });

        setLoadingTable(false);

      } catch (e: any) {
        handleApiError(e);
      } finally {
        setGroupsViewLoading(false);
      }
    };
    loadData();
    setThereIsUpdates(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, thereIsUpdates, showInactiveGroups]);


  useEffect(() => {
    // Atualiza o termo com debounce após 400ms
    const timeout = setTimeout(() => {
      setDebouncedGroupTerm(searchTerm);
    }, 400); // 400ms de debounce

    return () => clearTimeout(timeout); // Limpa o timeout anterior
  }, [searchTerm]); // Executa sempre que o usuário digitar algo


  const initialGroupList = useMemo(() => {
    if (debouncedGroupTerm === "") {
      return groupList; // Retorna os grupos iniciais quando o termo de busca está vazio
    } else {
      return [];
    }
  }, [debouncedGroupTerm, groupList]);


  useEffect(() => {
    const findGroupsBySearch = async () => {
      const company_id = user.company_visited?.id;
      if (!company_id)
        return redirectSessionExpired();

        if (debouncedGroupTerm === "") {
          // Usa os grupos memoizados quando o campo de busca está vazio
          setListGroupData({
            total_items: initialGroupList.length,
          total_pages: Math.ceil(initialGroupList.length / 10),
          current_page: 1,
          data: initialGroupList,
        });
        return;
      }

      // Faz a busca somente quando o termo com debounce for atualizado
      try {
        const response = await searchGroups({
          company_id,
          term: debouncedGroupTerm,
          groupsStatuses: showInactiveGroups ? [1, 2] : [1],
        });

        setListGroupData({
          total_items: response.total_items,
          total_pages: Math.ceil(response.total_items / 10),
          current_page: 1,
          data: response.data,
        });
      } catch (error) {
        handleApiError(error);
      }
    };

    findGroupsBySearch();
  }, [debouncedGroupTerm, user, initialGroupList, groupList, showInactiveGroups]);


  const redirectSessionExpired = () => {
    showToast(t('users.sessionExpired'), "info");
    setTimeout(() => {
      showToast(t('users.redirecting'), "info");
      setTimeout(() => SignOut("/admin/signup"), 5000);
    }, 2500);
  };

  const handleApiError = (error: any, options: ErrorHandlerOptions = {}) => {
    console.error(error);

    if (error?.response?.status === 401 || error?.response?.data?.error?.includes("token")) {
      return redirectSessionExpired();
    }

    showToast(
      getApiErrorMessage(
        error,
        'Ops! Algo não correu bem :(\n\nNosso time de engenheiros foi notificado e iremos corrigir essa questão o quanto antes. Para falar com nosso suporte, utilize o botão "Ajuda" na parte inferior da tela. '
      ),
      'error',
      15000
    );
    throw error;
  };

  const onSuccessToAddNewGroup = async (newItem: any) => {
    setThereIsUpdates(true)
  }

  const onErrorToAddNewGroup = (error: any) => {
    handleApiError(error, { redirectTo: '/admin/signup' });
  }


  const findGroupByInput = async () => {
    const company = user.company_visited?.id;
    if (!company)
      throw new Error("company_not_found");
    try {
      await searchGroups({
        company_id: company,
        term: searchTerm,
        groupsStatuses: showInactiveGroups ? [1, 2] : [1],
      });
    } catch (e) {
      handleApiError(e, { redirectTo: '/admin/signup' });
    }
  };

  const AddGroupButtonSkeleton = () => (
    <Button
      disabled
      disableRipple
      sx={{
        position: 'relative',
        textTransform: 'none',
        fontSize: '1em',
        padding: '.8em 2em',
        borderRadius: '2em',
        background: 'transparent',
        color: 'transparent',
        overflow: 'hidden',
        '@media (max-width:694px)': {
          width: '100%',
          fontSize: '14px',
        },
        '&.Mui-disabled': {
          opacity: 1,
          WebkitTextFillColor: 'unset',
          color: 'inherit',
        },
      }}
    >
      <Box sx={{ visibility: 'hidden', display: 'flex', alignItems: 'center', gap: '.5em' }}>
        <GroupAdd sx={{ fontSize: 15 }} />
        {t('groups.add')}
      </Box>

      <Box sx={{ position: 'absolute', inset: '-1px' }}>
        <Skeleton
          variant="rounded"
          animation="wave"
          width="100%"
          height="calc(100% + 4px)"
          sx={{ borderRadius: 'inherit', display: 'block' }}
        />
      </Box>
    </Button>
  );


  return (
    <div style={{ padding: "0 1.2em" }}>
      <ContainerConfigGroup>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2.5,
            flexWrap: 'nowrap',
            width: '100%',
            '@media (max-width:694px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
            },
          }}
        >
          <Box
            sx={{
              minWidth: '30%',
              '@media (min-width:695px) and (max-width:1199px)': {
                minWidth: '19%',
              },
              '@media (max-width:694px)': {
                width: '100%',
              },
              '@media (min-width:1200px)': {
                minWidth: '70%',
              },
            }}
          >
            <InputWrapper>
              {loadingTable ? (
                // Skeleton de carregamento - placeholder animado enquanto os dados carregam
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  height={56}
                  sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    // Em resoluções de notebook, aumenta um pouco a largura mínima do skeleton
                    "@media (min-width:695px) and (max-width:1199px)": {
                      minWidth: "300px",
                    },
                    // Em resoluções baixas, aumenta um pouco a altura
                    "@media (max-width:400px)": {
                      height: "64px",
                    },
                    // Em desktops maiores, expande a largura
                    "@media (min-width:1200px)": {
                      maxWidth: "400px",
                    },
                  }}
                />
              ) : (
                <TextField
                  variant="outlined"
                  value={searchTerm}
                  label={String(t('groups.searchLabel'))}
                  disabled={loadingTable}
                  placeholder={String(t('groups.searchPlaceholder'))}
                  onChange={(e) => {
                    setGroupTerm(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <>
                        <IconButton
                          onClick={findGroupByInput}
                        >
                          <Search />
                        </IconButton>
                      </>
                    ),
                  }}
                  sx={{
                    // Responsividade de largura
                    width: '100%',
                    "@media (min-width:1200px)": {
                      maxWidth: "70%",
                    },
                    
                    // Cor do texto do input
                    color: '#3C4043',
                    
                    // Cor de fundo branco
                    backgroundColor: '#FFF',
                    
                    // Borda arredondada suave
                    borderRadius: '0.375rem',
                    
                    // Muda cursor quando desabilitado
                    '&.Mui-disabled': {
                      cursor: 'wait',
                    },

                    // Estilo do texto digitado dentro do input
                    "& .MuiOutlinedInput-input": {
                      cursor: loadingTable ? "wait" : "text",
                    },

                    // Borda do input - muda cor baseado se tem texto ou não
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: searchTerm.length > 0 ? '#c9c9c9' : 'transparent',
                      borderRadius: '0.375rem',
                      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                    },
                    
                    // Cor da borda quando o input está focado
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    
                    // Cor da borda ao passar o mouse
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    
                    // Cor da borda quando focado E mouse sobre
                    '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    
                    // Cor da borda do root quando mouse sobre
                    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                  }}
                />
              )}
            </InputWrapper>
          </Box>

          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: 24,
              '@media (max-width:694px)': {
                display: 'none',
              },
            }}
          />

         
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '1.5em',
            justifyContent: 'flex-end',
            width: 'auto',
            flexDirection: 'row',
            '@media (max-width:768px)': {
              width: '100%',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
            },
            '@media (max-width:494px)': {
              alignItems: 'stretch',
            },
          }}
        >

          <StyledContainerButtonToggle>

            {loadingTable ? (
              <Skeleton variant="rounded" animation="wave" width={280} height={42} />
            ) : (
              <FormControlLabel
                sx={{
                  display: 'flex',
                  marginRight: 0,
                  whiteSpace: 'nowrap',
                  alignItems: 'center',
                  maxWidth: '100%',
                  width: 'auto',
                  justifyContent: 'flex-end',
                  '@media (max-width:320px)': {
                    justifyContent: 'center',
                  },
                  '@media (min-width:321px) and (max-width:494px)': {
                    justifyContent: 'flex-end',
                  },
                  '@media (max-width:494px)': {
                    width: '100%',
                  },
                }}
                control={
                  <Switch
                    checked={showInactiveGroups}
                    onChange={(_, checked) => {
                      if (groupsViewLoading) return;
                      setGroupsViewLoading(true);
                      setShowInactiveGroups(checked);
                    }}
                    disabled={groupsViewLoading}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>
                      {showInactiveGroups
                        ? t('groups.showingInactive')
                        : t('groups.showInactive')}
                    </span>
                    {groupsViewLoading && <CircularProgress size={14} thickness={6} />}
                  </Box>
                }
              />
            )}

            <ContainerButtonGroup>
              {loadingTable ? (
                <AddGroupButtonSkeleton />
              ) : (
                <Button
                  sx={{
                    background: 'linear-gradient(116deg, #04A5FF 16.33%, #2B81FF 83.67%)',
                    textTransform: 'none',
                    fontSize: '1em',
                    padding: '.8em 2em',
                    borderRadius: '2em',
                    '@media (max-width:768px)': {
                      width: '100%',
                    },
                    '@media (max-width:694px)': {
                      fontSize: '14px',
                    },
                  }}
                  startIcon={<GroupAdd sx={{ fontSize: 15 }} />}
                  variant="contained"
                  onClick={() => setShowModalNewGroup(true)}
                  disabled={groupsViewLoading}
                >
                  {t('groups.add')}
                </Button>
              )}
            </ContainerButtonGroup>

          </StyledContainerButtonToggle>

      
        </Box>
      </ContainerConfigGroup>

      <div className="mt-4">
        {loadingTable ? (
          <TableSkeleton columns={4} rows={8} />
        ) : (
          <GroupTable
            groups={listGroupData.data.map((group) => ({
              id: group.id,
              name: group.name,
              status: group.status,
              quantity_members: group?.users?.length ?? 0,
              users: group.users,
              responsible_users: group.responsible_users,
              company_id: group.company_id,
              created_at: group.created_at,
              updated_at: group.updated_at
            }))}
            totalItems={listGroupData.total_items}
            currentPage={listGroupData.current_page}
            searchTerm={debouncedGroupTerm}
            onPageChange={() => {}}
            setModalEditGroupData={setModalEditGroupData}
          />
        )}
      </div>


      {/* MODAIS */}
      <ModalAddGroup
        show={showModalNewGroup}
        userList={userList}
        onClose={() => {
          setShowModalNewGroup(false);
        }}
        onSuccess={onSuccessToAddNewGroup}
        onError={onErrorToAddNewGroup}
      />

      <ModalEditGroup
        key={modalEditGroupData.group_id || 'modal-edit-group-empty'}
        show={modalEditGroupData.show}
        group_id={modalEditGroupData.group_id}
        group_name={modalEditGroupData.group_name}
        group_status={modalEditGroupData.group_status}
        group_members={modalEditGroupData.group_members}
        responsible_users={modalEditGroupData.responsible_users}
        companyUserList={userList}
        onClose={() => {
          setModalEditGroupData({
            show: false,
            group_id: '',
            group_name: '',
            group_status: GroupStatus.active,
            group_members: [],
            responsible_users: [],
          });
          setThereIsUpdates(true);
        }}
      />
    </div>
  );
}
